/**
 * Registration Service - Real Backend Integration
 *
 * Handles asset creation using the correct one-step backend pattern:
 * POST /api/assets with FormData containing file + metadata
 * Also handles AI metadata generation workflow
 */

import { apiConfig } from '../../http/apiConfig';
import { apiRequest, publicRequest } from '../../http/request';
import {
    AIMetadataResponse,
    AssetCreationResponse,
    isAPIError,
    RegistrationFormData,
} from '../../shared/contracts/registration.contracts';
import type {
    AlgorhythmMetadataRequest,
    LookAssetRequest,
    LooksMetadataRequest,
    MoveAssetRequest,
    MovesMetadataRequest,
    SongMetadataRequest,
    SongAssetRequest,
    StarAssetRequest,
    StarMetadataRequest,
    WorldAssetRequest,
    WorldsMetadataRequest,
} from '../../contracts/openapi/assetContracts';
import { formatStructuredApiErrorResponse } from '../../utils/errorUtils';
import { conditionalError, conditionalLog } from '../../utils/logger';
import { LAYER_TO_VARIANTS_PATH, isVariantLayer } from '../../constants/variantUpload';
import { sanitizeMetadataForApi } from '../../constants/apiMetadataConstraints';

const API_BASE = apiConfig.baseURL || 'https://registry.dev.reviz.dev/api/v1';

export interface RegisterVariantPayload {
  baseAssetId: string;
  variantName: string;
  variantType: string;
}

type CanonicalCreatePayload =
  | SongAssetRequest
  | StarAssetRequest
  | LookAssetRequest
  | MoveAssetRequest
  | WorldAssetRequest;

const SONG_METADATA_KEYS = [
  'energy',
  'culturalOrigin',
  'style',
  'genre',
  'duration',
  'bpm',
  'key',
  'mood',
  'songName',
  'artistName',
  'albumName',
  'tempo',
  'timeSignature',
  'releaseYear',
  'albumArtUrl',
] as const;

const ALGORHYTHM_KEYS = [
  'targetAudience',
  'ageAppropriateness',
  'culturalContext',
  'musicalStyle',
  'performanceContext',
] as const;

export class RegistrationService {
  /**
   * Normalize API response to AssetCreationResponse. Payload is StoredAssetDTO (id, humanFriendlyName, machineFriendlyAddress).
   * Backend may return DTO at top level or as { data: dto }.
   */
  private normalizeCreationResponse(resultData: any): AssetCreationResponse {
    const payload = resultData?.data ?? resultData;
    return { success: true, data: payload };
  }

  /**
   * Health check to verify backend connectivity
   */
  async checkBackendHealth(): Promise<{ success: boolean; message: string; responseTime?: number }> {
    const startTime = Date.now();
    try {
      conditionalLog('🏥 [HEALTH CHECK] Testing backend connectivity...');
      const response = await publicRequest<string>({
        url: `${API_BASE}/version`,
        method: 'GET',
        timeout: 10000,
        responseType: 'text',
        validateStatus: () => true,
      });

      const responseTime = Date.now() - startTime;

      if (response.status >= 200 && response.status < 300) {
        console.log(`✅ [HEALTH CHECK] Backend is healthy (${responseTime}ms)`);
        return { success: true, message: `Backend is healthy (${responseTime}ms)`, responseTime };
      } else {
        console.error(`⚠️ [HEALTH CHECK] Backend returned ${response.status} (${responseTime}ms)`);
        return { success: false, message: `Backend returned HTTP ${response.status}`, responseTime };
      }
    } catch (error: any) {
      const responseTime = Date.now() - startTime;
      console.error(`❌ [HEALTH CHECK] Failed after ${responseTime}ms:`, error.message);

      if (error.name === 'AbortError') {
        return { success: false, message: 'Health check timeout - backend may be down' };
      }
      if (error.message?.includes('ERR_NAME_NOT_RESOLVED')) {
        return { success: false, message: 'DNS resolution failed - check internet connection' };
      }
      if (error.message?.includes('ERR_INTERNET_DISCONNECTED')) {
        return { success: false, message: 'Internet connection issue detected' };
      }

      return { success: false, message: `Network error: ${error.message}` };
    }
  }

  private async makeRequest(endpoint: string, options: RequestInit = {}, retryCount: number = 0): Promise<{ data: any; status: number }> {
    const url = `${API_BASE}${endpoint}`;

    try {
      conditionalLog(`🌐 [NETWORK] Making ${options.method || 'GET'} request to ${url} (attempt ${retryCount + 1})`);
      const response = await apiRequest<string>({
        url,
        method: (options.method as any) || 'GET',
        data: options.body as any,
        headers: options.headers as Record<string, string> | undefined,
        timeout: 120000,
        responseType: 'text',
        validateStatus: () => true,
      });

      if (response.status < 200 || response.status >= 300) {
        console.error(`❌ [NETWORK] HTTP ${response.status} for ${url}`);
      }

      // Handle response based on content type
      let data;
      const rawContentType = response.headers['content-type'];
      const contentType =
        typeof rawContentType === 'string'
          ? rawContentType
          : Array.isArray(rawContentType)
            ? rawContentType.join(';')
            : '';
      const textContent =
        typeof response.data === 'string'
          ? response.data
          : JSON.stringify(response.data);

      if (contentType.includes('application/json')) {
        try {
          data = JSON.parse(textContent);
        } catch (jsonError) {
          console.error(`❌ [NETWORK] Failed to parse JSON response from ${url}:`, jsonError);
          throw new Error(`Invalid JSON response from server: ${textContent.substring(0, 200)}...`);
        }
      } else {
        conditionalLog(`⚠️ [NETWORK] Non-JSON response from ${url} (${contentType}):`, textContent.substring(0, 200));

        // Try to parse as JSON anyway in case content-type header is wrong
        try {
          data = JSON.parse(textContent);
        } catch {
          // If it's not JSON, wrap the text content
          data = { message: textContent, rawResponse: true };
        }
      }

      // Debug logging for response analysis
      if (response.status < 200 || response.status >= 300) {
        console.error(`🔍 [NETWORK DEBUG] Status ${response.status} response data:`, JSON.stringify(data, null, 2));
      }

      return { data, status: response.status };

    } catch (error: any) {
      if (error.code === 'ECONNABORTED') {
        console.error(`⏰ [NETWORK] Request timeout after 2 minutes for ${url}`);
        throw new Error('Request timeout - the server is taking too long to respond. Please try again.');
      }

      if (error.message?.includes('ERR_NETWORK') || error.message?.includes('ERR_INTERNET_DISCONNECTED')) {
        console.error(`🔌 [NETWORK] Network connectivity issue: ${error.message}`);
        throw new Error('Network connectivity issue - please check your internet connection and try again.');
      }

      if (error.message?.includes('ERR_NAME_NOT_RESOLVED')) {
        console.error(`🌐 [NETWORK] DNS resolution failed: ${error.message}`);
        throw new Error('Unable to connect to the server - please check your internet connection.');
      }

      if (error.message?.includes('Failed to fetch')) {
        // Retry logic for network failures
        const maxRetries = 3;
        if (retryCount < maxRetries) {
          conditionalLog(`🔄 [NETWORK] Retrying request (${retryCount + 1}/${maxRetries}) after network error`);
          await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds before retry
          return this.makeRequest(endpoint, options, retryCount + 1);
        } else {
          console.error(`❌ [NETWORK] Max retries exceeded for ${url}`);
          throw new Error('Network error - please check your connection and try again.');
        }
      }

      console.error(`❌ [NETWORK] Unexpected error for ${url}:`, error);
      throw error;
    }
  }

  /**
   * Generate AI metadata for uploaded file
   * Step 1 of the registration process
   *
   * FIXED: Now uses aiContractService which properly handles file URLs and placeholders
   */
  async generateAIMetadata(formData: RegistrationFormData): Promise<AIMetadataResponse> {
    const startTime = performance.now();
    conditionalLog('🤖 [REGISTRATION SERVICE] Generating AI metadata via AI Contract Service');

    // Ensure required fields are present
    if (!formData.layer) {
      throw new Error('Layer is required for AI metadata generation');
    }

    if (!formData.creatorDescription) {
      throw new Error('Creator description is required for AI metadata generation');
    }

    // Import aiContractService dynamically to avoid circular dependencies
    const { default: aiContractService } = await import('../aiContractService');

    try {
      // Debug: Log file information
      conditionalLog('🔍 [REGISTRATION SERVICE] File check:', {
        hasFile: !!formData.file,
        fileName: formData.file?.name,
        fileSize: formData.file?.size,
        fileType: formData.file?.type,
        fileIsFileInstance: formData.file instanceof File,
      });

      // Build AI generation request - now passes File object directly for multipart/form-data
      const aiRequest = {
        layer: formData.layer,
        creatorDescription: formData.creatorDescription,
        file: formData.file, // Pass File object directly for multipart/form-data
        // Do NOT include fileUrl when we have a File object - let the service handle it
        additionalContext: {
          category: formData.category,
          subcategory: formData.subcategory,
          categoryName: formData.categoryName,
          subcategoryName: formData.subcategoryName,
          fileName: formData.file?.name,
          fileType: formData.file?.type,
          fileSize: formData.file?.size,
        }
      };

      conditionalLog('🚀 [AI CONTRACT] Calling aiContractService.generateMetadata with File object...');
      conditionalLog('🚀 [AI CONTRACT] Request object:', {
        layer: aiRequest.layer,
        creatorDescription: aiRequest.creatorDescription,
        hasFile: !!aiRequest.file,
        fileName: aiRequest.file?.name,
      });
      const contractResponse = await aiContractService.generateMetadata(aiRequest);

      const duration = Math.round(performance.now() - startTime);
      conditionalLog(`⏱️ [REGISTRATION SERVICE] AI service completed in ${duration}ms`);

      if (duration > 30000) {
        conditionalLog(`⚠️ [REGISTRATION SERVICE] SLOW AI SERVICE: ${duration}ms (>30s)`);
      }

      if (!contractResponse.success) {
        throw new Error(contractResponse.error?.message || 'AI metadata generation failed');
      }

      // Transform the AI contract response to match expected AIMetadataResponse format
      const transformedResponse: AIMetadataResponse = {
        success: true,
        data: {
          description: contractResponse.data.description,
          tags: contractResponse.data.tags,
          layerMetadata: contractResponse.data.layerMetadata,
        },
        message: `AI generation completed using ${contractResponse.processingMethod} in ${contractResponse.processingTimeMs}ms`,
      };

      console.log('✅ [REGISTRATION SERVICE] AI metadata generated successfully');

      return transformedResponse;

    } catch (error) {
      console.error('❌ [REGISTRATION SERVICE] Error generating AI metadata:', error);
      throw error;
    }
  }

  /**
   * Create asset using the backend's one-step process
   * Step 2 of the registration process (after AI metadata generation)
   */
  /**
   * Safe JSON stringify that handles circular references
   */
  private safeJSONStringify(obj: any): string {
    const seen = new WeakSet();
    return JSON.stringify(obj, (key, value) => {
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) {
          return '[Circular Reference]';
        }
        seen.add(value);
      }
      // Filter out problematic objects
      if (value && typeof value === 'object') {
        // Skip DOM elements, window objects, and other non-serializable objects
        if (value instanceof Element || value instanceof Window || value instanceof Document) {
          return undefined;
        }
        // Skip functions
        if (typeof value === 'function') {
          return undefined;
        }
      }
      return value;
    });
  }

  private isNonEmptyString(value: unknown): value is string {
    return typeof value === 'string' && /\S/.test(value);
  }

  private isStringArray(value: unknown): value is string[] {
    return Array.isArray(value) && value.every(item => typeof item === 'string');
  }

  private isNonEmptyStringArray(value: unknown): value is string[] {
    return this.isStringArray(value) && value.length > 0 && value.every(item => /\S/.test(item));
  }

  private isOptionalStringArray(value: unknown): value is string[] | undefined {
    return value === undefined || this.isStringArray(value);
  }

  private isOptionalNumber(value: unknown): value is number | undefined {
    return value === undefined || typeof value === 'number';
  }

  private isOptionalBoolean(value: unknown): value is boolean | undefined {
    return value === undefined || typeof value === 'boolean';
  }

  private assertNoUnexpectedKeys(
    value: Record<string, unknown>,
    allowedKeys: readonly string[],
    context: string
  ): void {
    const unexpectedKeys = Object.keys(value).filter(key => !allowedKeys.includes(key));
    if (unexpectedKeys.length > 0) {
      throw new Error(`${context} is invalid: unexpected fields ${unexpectedKeys.join(', ')}.`);
    }
  }

  private assertCreateCommonFields(formData: RegistrationFormData): {
    category: string;
    subcategory: string;
    description: string;
    creatorDescription?: string;
    tags: string[];
  } {
    const { category, subcategory, description, creatorDescription, tags } = formData as RegistrationFormData & {
      description?: unknown;
      tags?: unknown;
    };

    if (!this.isNonEmptyString(category)) {
      throw new Error('Create payload is invalid: category must be a non-empty string.');
    }
    if (!this.isNonEmptyString(subcategory)) {
      throw new Error('Create payload is invalid: subcategory must be a non-empty string.');
    }
    if (!this.isNonEmptyString(description)) {
      throw new Error('Create payload is invalid: description must be a non-empty string.');
    }
    if (!this.isNonEmptyStringArray(tags)) {
      throw new Error('Create payload is invalid: tags must be a non-empty array of non-empty strings.');
    }
    if (creatorDescription !== undefined && typeof creatorDescription !== 'string') {
      throw new Error('Create payload is invalid: creatorDescription must be a string when provided.');
    }

    return {
      category,
      subcategory,
      description,
      creatorDescription,
      tags,
    };
  }

  private asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  }

  private sanitizeStringArray(value: unknown): string[] | undefined {
    if (Array.isArray(value)) {
      const result = value
        .map(item => (typeof item === 'string' ? item.trim() : String(item).trim()))
        .filter(Boolean);
      return result.length > 0 ? result : undefined;
    }

    if (this.isNonEmptyString(value)) {
      return [value.trim()];
    }

    return undefined;
  }

  private assignIfDefined(
    target: Record<string, unknown>,
    key: string,
    value: unknown
  ): void {
    if (value === undefined || value === null) {
      return;
    }

    if (typeof value === 'string') {
      if (!this.isNonEmptyString(value)) {
        return;
      }
      target[key] = value.trim();
      return;
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        return;
      }
      target[key] = value;
      return;
    }

    target[key] = value;
  }

  private buildAlgorhythmMetadataRequest(source: Record<string, unknown>): Partial<AlgorhythmMetadataRequest> | undefined {
    const direct = this.asRecord(source.algorhythmMetadata);
    const metadata: Partial<AlgorhythmMetadataRequest> = {};

    ALGORHYTHM_KEYS.forEach(key => {
      const value = this.sanitizeStringArray(direct[key] ?? source[key]);
      if (value) {
        switch (key) {
          case 'targetAudience':
            metadata.targetAudience = value as AlgorhythmMetadataRequest['targetAudience'];
            break;
          case 'ageAppropriateness':
            metadata.ageAppropriateness = value;
            break;
          case 'culturalContext':
            metadata.culturalContext = value as AlgorhythmMetadataRequest['culturalContext'];
            break;
          case 'musicalStyle':
            metadata.musicalStyle = value;
            break;
          case 'performanceContext':
            metadata.performanceContext = value as AlgorhythmMetadataRequest['performanceContext'];
            break;
          default:
            break;
        }
      }
    });

    return Object.keys(metadata).length > 0
      ? metadata
      : undefined;
  }

  private canonicalizeSongMetadata(metadata: unknown): SongMetadataRequest {
    const source = this.asRecord(metadata);
    const canonical: Partial<Omit<SongMetadataRequest, 'algorhythmMetadata'>> & {
      algorhythmMetadata?: Partial<AlgorhythmMetadataRequest>;
    } = {};

    SONG_METADATA_KEYS.forEach(key => {
      const rawValue =
        key === 'genre' || key === 'mood'
          ? this.sanitizeStringArray(source[key])
          : source[key];
      this.assignIfDefined(canonical as Record<string, unknown>, key, rawValue);
    });

    const algorhythmMetadata = this.buildAlgorhythmMetadataRequest(source);
    if (algorhythmMetadata) {
      canonical.algorhythmMetadata = algorhythmMetadata;
    }

    return this.assertSongMetadata(canonical);
  }

  private canonicalizeLookMetadata(metadata: unknown): LooksMetadataRequest {
    const source = this.asRecord(metadata);
    const canonical: Partial<LooksMetadataRequest> = {};

    [
      'energy',
      'culturalOrigin',
      'outfitName',
      'styleCategory',
      'style',
      'gender',
      'occasion',
      'colorScheme',
      'primaryGarment',
      'mood',
      'primaryColors',
      'patterns',
      'targetAudience',
      'performanceContext',
      'culturalContext',
      'materials',
      'seasonality',
      'colorPalette',
      'timeOfDay',
      'accessories',
      'jewelryType',
      'brandNames',
      'priceRange',
      'formality',
    ].forEach(key => {
      const rawValue =
        key === 'mood' ||
        key === 'primaryColors' ||
        key === 'patterns' ||
        key === 'targetAudience' ||
        key === 'performanceContext' ||
        key === 'culturalContext' ||
        key === 'materials' ||
        key === 'accessories' ||
        key === 'brandNames'
          ? this.sanitizeStringArray(source[key])
          : source[key];
      this.assignIfDefined(canonical as Record<string, unknown>, key, rawValue);
    });

    return this.assertLookMetadata(canonical);
  }

  private assertSongMetadata(metadata: unknown): SongMetadataRequest {
    if (!metadata || typeof metadata !== 'object') {
      throw new Error('Song create payload is invalid: metadata is required.');
    }

    const typed = metadata as SongMetadataRequest;
    this.assertNoUnexpectedKeys(typed as unknown as Record<string, unknown>, [
      'energy',
      'culturalOrigin',
      'style',
      'genre',
      'duration',
      'bpm',
      'key',
      'mood',
      'songName',
      'artistName',
      'albumName',
      'tempo',
      'timeSignature',
      'releaseYear',
      'albumArtUrl',
      'algorhythmMetadata',
    ], 'Song create payload metadata');

    if (
      !this.isNonEmptyString(typed.songName) ||
      !this.isNonEmptyString(typed.artistName) ||
      !this.isNonEmptyString(typed.tempo) ||
      !this.isNonEmptyStringArray(typed.genre) ||
      !this.isNonEmptyStringArray(typed.mood)
    ) {
      throw new Error('Song create payload is invalid: metadata must include songName, artistName, tempo, genre, and mood.');
    }

    if (
      !this.isOptionalNumber(typed.duration) ||
      !this.isOptionalNumber(typed.bpm) ||
      !this.isOptionalNumber(typed.releaseYear)
    ) {
      throw new Error('Song create payload is invalid: numeric metadata fields must be numbers when provided.');
    }

    if (
      !this.isOptionalStringArray(typed.algorhythmMetadata?.ageAppropriateness) ||
      !this.isOptionalStringArray(typed.algorhythmMetadata?.musicalStyle)
    ) {
      throw new Error('Song create payload is invalid: algorhythmMetadata arrays must be arrays of strings.');
    }

    return typed;
  }

  private assertStarMetadata(metadata: unknown): StarMetadataRequest {
    if (!metadata || typeof metadata !== 'object') {
      throw new Error('Star create payload is invalid: metadata is required.');
    }

    const typed = metadata as StarMetadataRequest;
    this.assertNoUnexpectedKeys(typed as unknown as Record<string, unknown>, [
      'energy',
      'culturalOrigin',
      'starName',
      'realName',
      'gender',
      'ageGroup',
      'ethnicity',
      'bodyType',
      'height',
      'hairColor',
      'hairLength',
      'hairStyle',
      'eyeColor',
      'skinTone',
      'archetype',
      'mood',
      'musicalStyle',
      'careerStage',
      'personalityTraits',
      'performanceContext',
      'makeupType',
      'colorPalette',
      'hairTexture',
      'hairTreatment',
      'hairAccessories',
      'eyeMakeup',
      'lipMakeup',
      'faceMakeup',
      'makeupEffects',
    ], 'Star create payload metadata');
    if (
      !this.isNonEmptyString(typed.starName) ||
      !this.isNonEmptyString(typed.ethnicity) ||
      !this.isNonEmptyStringArray(typed.mood) ||
      !typed.gender ||
      !typed.ageGroup
    ) {
      throw new Error('Star create payload is invalid: metadata must include starName, gender, ageGroup, ethnicity, and mood.');
    }

    if (
      !this.isOptionalStringArray(typed.musicalStyle) ||
      !this.isOptionalStringArray(typed.personalityTraits)
    ) {
      throw new Error('Star create payload is invalid: optional array fields must be arrays of strings.');
    }

    return typed;
  }

  private assertLookMetadata(metadata: unknown): LooksMetadataRequest {
    if (!metadata || typeof metadata !== 'object') {
      throw new Error('Look create payload is invalid: metadata is required.');
    }

    const typed = metadata as LooksMetadataRequest;
    this.assertNoUnexpectedKeys(typed as unknown as Record<string, unknown>, [
      'energy',
      'culturalOrigin',
      'outfitName',
      'styleCategory',
      'style',
      'gender',
      'occasion',
      'colorScheme',
      'primaryGarment',
      'mood',
      'primaryColors',
      'patterns',
      'targetAudience',
      'performanceContext',
      'culturalContext',
      'materials',
      'seasonality',
      'colorPalette',
      'timeOfDay',
      'accessories',
      'jewelryType',
      'brandNames',
      'priceRange',
      'formality',
    ], 'Look create payload metadata');
    if (
      !this.isNonEmptyString(typed.outfitName) ||
      !this.isNonEmptyString(typed.styleCategory) ||
      !this.isNonEmptyString(typed.style) ||
      !typed.gender ||
      !this.isNonEmptyString(typed.occasion) ||
      !this.isNonEmptyString(typed.colorScheme) ||
      !this.isNonEmptyString(typed.primaryGarment) ||
      !this.isNonEmptyStringArray(typed.mood) ||
      !this.isNonEmptyStringArray(typed.primaryColors) ||
      !this.isNonEmptyString(typed.formality)
    ) {
      throw new Error('Look create payload is invalid: metadata must match the OpenAPI required fields.');
    }

    if (
      !this.isOptionalStringArray(typed.patterns) ||
      !this.isOptionalStringArray(typed.materials) ||
      !this.isOptionalStringArray(typed.accessories) ||
      !this.isOptionalStringArray(typed.brandNames)
    ) {
      throw new Error('Look create payload is invalid: optional list fields must be arrays of strings.');
    }

    return typed;
  }

  private assertMoveMetadata(metadata: unknown): MovesMetadataRequest {
    if (!metadata || typeof metadata !== 'object') {
      throw new Error('Move create payload is invalid: metadata is required.');
    }

    const typed = metadata as MovesMetadataRequest;
    this.assertNoUnexpectedKeys(typed as unknown as Record<string, unknown>, [
      'energy',
      'culturalOrigin',
      'tempo',
      'moveName',
      'danceStyle',
      'difficulty',
      'mood',
      'bodyParts',
      'choreographerName',
      'substyle',
      'complexity',
      'intensity',
      'pace',
      'movementStyle',
      'spaceRequired',
      'groupSize',
      'skillLevelRequired',
      'costumeRequirements',
      'targetAudience',
      'performanceContext',
      'audienceType',
      'bpmRangeMin',
      'bpmRangeMax',
      'floorWork',
      'partnered',
      'signatureMoves',
      'techniquesRequired',
      'propsNeeded',
    ], 'Move create payload metadata');
    if (
      !this.isNonEmptyString(typed.tempo) ||
      !this.isNonEmptyString(typed.moveName) ||
      !this.isNonEmptyString(typed.danceStyle) ||
      !this.isNonEmptyString(typed.difficulty) ||
      !this.isNonEmptyStringArray(typed.mood)
    ) {
      throw new Error('Move create payload is invalid: metadata must include tempo, moveName, danceStyle, difficulty, and mood.');
    }

    if (
      !this.isOptionalStringArray(typed.bodyParts) ||
      !this.isOptionalStringArray(typed.signatureMoves) ||
      !this.isOptionalStringArray(typed.techniquesRequired) ||
      !this.isOptionalStringArray(typed.propsNeeded) ||
      !this.isOptionalNumber(typed.bpmRangeMin) ||
      !this.isOptionalNumber(typed.bpmRangeMax) ||
      !this.isOptionalBoolean(typed.floorWork) ||
      !this.isOptionalBoolean(typed.partnered)
    ) {
      throw new Error('Move create payload is invalid: optional move fields must match the OpenAPI schema.');
    }

    return typed;
  }

  private assertWorldMetadata(metadata: unknown): WorldsMetadataRequest {
    if (!metadata || typeof metadata !== 'object') {
      throw new Error('World create payload is invalid: metadata is required.');
    }

    const typed = metadata as WorldsMetadataRequest;
    this.assertNoUnexpectedKeys(typed as unknown as Record<string, unknown>, [
      'energy',
      'culturalOrigin',
      'style',
      'mood',
      'timeOfDay',
      'season',
      'versatility',
      'targetAudience',
      'performanceContext',
      'culturalContext',
      'worldName',
      'locationType',
      'lighting',
      'dominantColors',
      'atmosphere',
      'weather',
      'setting',
      'environmentType',
      'lightingStyle',
      'spatialSize',
      'settingCategory',
      'specificLocation',
      'spaceSize',
      'layout',
      'depth',
      'colorPalette',
      'lightingDirection',
      'geographicRegion',
      'architecturalStyle',
      'backdropType',
      'productionValue',
      'technicalComplexity',
      'movementSpace',
      'audioAcoustics',
      'crowdCapacity',
    ], 'World create payload metadata');
    if (
      !this.isNonEmptyString(typed.worldName) ||
      !this.isNonEmptyString(typed.locationType) ||
      !this.isNonEmptyStringArray(typed.mood)
    ) {
      throw new Error('World create payload is invalid: metadata must include worldName, locationType, and mood.');
    }

    if (!this.isOptionalStringArray(typed.dominantColors)) {
      throw new Error('World create payload is invalid: dominantColors must be an array of strings when provided.');
    }

    return typed;
  }

  private buildSongAssetRequest(formData: RegistrationFormData): SongAssetRequest {
    const common = this.assertCreateCommonFields(formData);
    const canonicalMetadata = this.canonicalizeSongMetadata(formData.songMetadata);
    return {
      ...common,
      metadata: canonicalMetadata,
    };
  }

  private buildStarAssetRequest(formData: RegistrationFormData, aiMetadata?: AIMetadataResponse): StarAssetRequest {
    const common = this.assertCreateCommonFields(formData);
    const aiDescription = aiMetadata?.data?.description;
    const payload: StarAssetRequest = {
      ...common,
      metadata: this.assertStarMetadata(formData.starMetadata),
    };
    if (this.isNonEmptyString(aiDescription)) {
      payload.aiDescription = aiDescription;
    }
    return payload;
  }

  private buildLookAssetRequest(formData: RegistrationFormData, aiMetadata?: AIMetadataResponse): LookAssetRequest {
    const common = this.assertCreateCommonFields(formData);
    const aiDescription = aiMetadata?.data?.description;
    const canonicalMetadata = this.canonicalizeLookMetadata(formData.looksMetadata);
    const payload: LookAssetRequest = {
      ...common,
      metadata: canonicalMetadata,
    };
    if (this.isNonEmptyString(aiDescription)) {
      payload.aiDescription = aiDescription;
    }
    return payload;
  }

  private buildMoveAssetRequest(formData: RegistrationFormData, aiMetadata?: AIMetadataResponse): MoveAssetRequest {
    const common = this.assertCreateCommonFields(formData);
    const aiDescription = aiMetadata?.data?.description;
    const payload: MoveAssetRequest = {
      ...common,
      metadata: this.assertMoveMetadata(formData.movesMetadata),
    };
    if (this.isNonEmptyString(aiDescription)) {
      payload.aiDescription = aiDescription;
    }
    return payload;
  }

  private buildWorldAssetRequest(formData: RegistrationFormData, aiMetadata?: AIMetadataResponse): WorldAssetRequest {
    const common = this.assertCreateCommonFields(formData);
    const aiDescription = aiMetadata?.data?.description;
    const payload: WorldAssetRequest = {
      ...common,
      metadata: this.assertWorldMetadata(formData.worldsMetadata),
    };
    if (this.isNonEmptyString(aiDescription)) {
      payload.aiDescription = aiDescription;
    }
    return payload;
  }

  private buildAssetFormData(file: File, assetData: CanonicalCreatePayload): FormData {
    const formData = new FormData();
    formData.append('assetFile', file);
    formData.append('assetData', JSON.stringify(assetData));
    return formData;
  }

  /**
   * Helper method to clean metadata object - removes circular references and DOM objects
   */
  private cleanMetadataForSubmission(metadata: any): any {
    // Create a clean copy without circular references or DOM objects
    const clean: any = {};

    // Comprehensive list of safe keys for all layer types
    const safeKeys = [
      // Songs (G layer)
      'title', 'artist', 'album', 'genre', 'year', 'duration',
      'bpm', 'key', 'mood', 'energy', 'description', 'tags',
      'composer', 'publisher', 'isrc', 'iswc', 'lyrics',
      'songName', 'artistName', 'albumName', 'tempo', 'timeSignature',
      'releaseYear', 'culturalOrigin', 'language', 'targetAudience',
      'albumArt', 'albumArtUrl', 'artworkUrl', // Album art URLs
      // Looks (L layer)
      'outfitName', 'lookName', 'styleCategory', 'style', 'occasion',
      'colorScheme', 'primaryGarment', 'primaryColors', 'secondaryColors',
      'patterns', 'materials', 'seasonality', 'timeOfDay', 'accessories',
      'jewelryType', 'brandNames', 'priceRange', 'energyLevel', 'formality',
      'assetType', 'layerType', 'compatibilityScore', 'category', 'subcategory',
      'designer', 'season', 'targetGender', 'targetAge', 'formalityLevel',
      'versatility', 'trendiness', 'comfort', 'durability', 'careInstructions',
      'sustainability', 'culturalSignificance', 'baseLookId', 'variantName', 'variantType',
      // Stars (S layer)
      'starName', 'characterName', 'personality', 'skills', 'specialAbilities',
      // Moves (M layer)
      'moveName', 'danceStyle', 'difficulty', 'bodyParts', 'musicGenre',
      'danceClassification', 'movementPatterns', 'culturalContext', 'energyTempo',
      // Worlds (W layer)
      'worldName', 'locationType', 'settingCategory', 'spaceSize', 'layout',
      'depth', 'lighting', 'lighting_direction', 'color_palette', 'dominant_colors',
      'atmosphere', 'vibe', 'weather', 'time_of_day', 'geographic_region',
      'architectural_style', 'backdrop_type', 'production_value', 'technical_complexity',
      'movement_space', 'audio_acoustics', 'crowd_capacity', 'specific_location',
      // Common fields
      'creatorDescription', 'algorhythmMetadata',
    ];

    // Fields that should be converted from array to comma-separated string
    // These are fields where backend expects string but AI generates array
    const arrayToStringFields = [
      'brandNames', 'primaryColors', 'secondaryColors', 'patterns', 'materials',
      'accessories', 'jewelryType', 'dominant_colors', 'bodyParts', 'skills',
      'specialAbilities', 'movementPatterns',
    ];

    for (const key of safeKeys) {
      if (metadata[key] !== undefined && metadata[key] !== null) {
        // Check if this field should be converted from array to string
        if (arrayToStringFields.includes(key) && Array.isArray(metadata[key])) {
          // Convert array to comma-separated string for backend compatibility
          clean[key] = metadata[key].join(', ');
          conditionalLog(`🔄 [METADATA] Converted ${key} from array to string: ${clean[key]}`);
        } else if (typeof metadata[key] === 'string' ||
            typeof metadata[key] === 'number' ||
            typeof metadata[key] === 'boolean') {
          clean[key] = metadata[key];
        } else if (Array.isArray(metadata[key]) &&
                   metadata[key].every((item: any) => typeof item === 'string')) {
          // Keep as array for fields that backend expects as arrays (like tags)
          clean[key] = metadata[key];
        } else if (typeof metadata[key] === 'object' && !Array.isArray(metadata[key])) {
          // Handle nested objects like algorhythmMetadata
          clean[key] = metadata[key];
        }
      }
    }

    return clean;
  }

  /**
   * TEMPORARY: Map backend field names to frontend expectations
   * This provides compatibility while backend team aligns field names
   */
  private mapBackendToFrontendFields(backendData: any): any {
    if (!backendData) return backendData;

    const mapped = { ...backendData };

    // Map field name differences
    if (backendData.energyLevel !== undefined) {
      mapped.energy = backendData.energyLevel;
      conditionalLog('🔄 [FIELD MAPPING] energyLevel → energy:', backendData.energyLevel);
    }

    if (backendData.vocalRange !== undefined) {
      mapped.vocalType = backendData.vocalRange;
      conditionalLog('🔄 [FIELD MAPPING] vocalRange → vocalType:', backendData.vocalRange);
    }

    return mapped;
  }

  /**
   * PHASE 1 OPTIMIZATION: Consolidate layer metadata to prevent duplication
   * Merges user metadata with AI metadata, prioritizing user input
   */
  private consolidateLayerMetadata(formData: RegistrationFormData, aiLayerMetadata?: any): any | null {
    // CRITICAL FIX: Use layer-specific metadata fields for all layers
    let userMetadata;
    if (formData.layer === 'G') {
      userMetadata = formData.songMetadata || formData.layerMetadata;
    } else if (formData.layer === 'S') {
      userMetadata = formData.starMetadata || formData.layerMetadata;
    } else if (formData.layer === 'L') {
      // PHASE 1 FIX (L-4): Support looksMetadata for Looks layer
      userMetadata = formData.looksMetadata || formData.layerMetadata;
    } else if (formData.layer === 'M') {
      // PHASE 1 FIX (M-1): Support movesMetadata for Moves layer
      userMetadata = formData.movesMetadata || formData.layerMetadata;
    } else if (formData.layer === 'W') {
      // PHASE 1 FIX (W-3, W-4): Support worldsMetadata for Worlds layer
      userMetadata = formData.worldsMetadata || formData.layerMetadata;
    } else {
      // For other layers (B, P, C), use layerMetadata only
      userMetadata = formData.layerMetadata;
    }

    // If no metadata exists, return null
    if (!userMetadata && !aiLayerMetadata) {
      return null;
    }

    // Start with user metadata as base (user input takes priority)
    const consolidated = { ...userMetadata };

    // Enhance with AI metadata only for missing fields
    if (aiLayerMetadata) {
      Object.keys(aiLayerMetadata).forEach(key => {
        // Only use AI data if user hasn't provided this field
        if (!consolidated[key] ||
            (typeof consolidated[key] === 'string' && consolidated[key].trim() === '') ||
            (Array.isArray(consolidated[key]) && consolidated[key].length === 0)) {
          consolidated[key] = aiLayerMetadata[key];
        }
      });
    }

    // Filter out empty fields to prevent bloat
    const filtered: any = {};
    Object.entries(consolidated).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        if (Array.isArray(value) && value.length > 0) {
          filtered[key] = value;
        } else if (!Array.isArray(value)) {
          filtered[key] = value;
        }
      }
    });

    return Object.keys(filtered).length > 0 ? filtered : null;
  }

  // Helper method to convert file to base64
  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      conditionalLog('🔄 [BASE64] Converting file to base64:', file.name, file.type, file.size, 'bytes');
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        conditionalLog('✅ [BASE64] Conversion successful, length:', result.length, 'characters');
        resolve(result);
      };
      reader.onerror = error => {
        console.error('❌ [BASE64] Conversion failed:', error);
        reject(error);
      };
    });
  }

  async createAsset(formData: RegistrationFormData, aiMetadata?: AIMetadataResponse): Promise<AssetCreationResponse> {
    conditionalLog('🚀 [REGISTRATION SERVICE] Creating asset with JSON format as per documentation');

    if (!formData.file) {
      throw new Error('File is required for asset creation');
    }

    // Convert file to base64 for JSON payload
    const fileBase64 = await this.fileToBase64(formData.file);

    // ANTI-BLOAT DEBUG: Check what metadata fields are in formData
    const formDataMetadataFields = [];
    if (formData.songMetadata) formDataMetadataFields.push('songMetadata');
    if (formData.starMetadata) formDataMetadataFields.push('starMetadata');
    if (formData.looksMetadata) formDataMetadataFields.push('looksMetadata');
    if (formData.movesMetadata) formDataMetadataFields.push('movesMetadata');
    if (formData.worldsMetadata) formDataMetadataFields.push('worldsMetadata');
    if (formData.layerMetadata) formDataMetadataFields.push('layerMetadata');

    if (formDataMetadataFields.length > 1) {
      console.error('❌ [ANTI-BLOAT VIOLATION] Multiple metadata fields detected:', formDataMetadataFields);
    }

    conditionalLog('🔍 [JSON PAYLOAD DEBUG] Key formData fields:', {
      layer: formData.layer,
      category: formData.category,
      subcategory: formData.subcategory,
      creatorDescription: formData.creatorDescription,
      humanFriendlyName: formData.humanFriendlyName,
      assetType: formData.assetType,
      hasLayerMetadata: !!formData.layerMetadata,
      hasSongMetadata: !!formData.songMetadata,
      hasStarMetadata: !!formData.starMetadata,
      hasLooksMetadata: !!formData.looksMetadata,
      hasMovesMetadata: !!formData.movesMetadata,
      hasWorldsMetadata: !!formData.worldsMetadata
    });

    // CRITICAL: Build JSON payload with ONLY allowed root fields
    // For G layer: name, layer, category, subcategory, source, fileUrl, humanFriendlyName, creatorDescription (optional)
    const jsonPayload: any = {
      // Core required fields ONLY
      layer: formData.layer || '',
      category: formData.category || '',
      subcategory: formData.subcategory || '',
      source: 'frontend-registration',
      humanFriendlyName: formData.humanFriendlyName || 'Untitled Asset',
      assetType: formData.assetType || 'base',

      // MONGODB COMPATIBILITY: Add required fields based on backend schema
      name: this.generateAssetName(formData), // Generate HFN that backend expects
      tags: [], // Will be populated later with AI tags

      // File data as base64
      fileData: fileBase64,
      fileName: formData.file.name,
      fileType: formData.file.type,
      fileSize: formData.file.size,

      // Placeholder file URL until backend returns upload URL (replaces deprecated gcpStorageUrl)
      fileUrl: `https://storage.googleapis.com/nna-assets/placeholder-${Date.now()}-${formData.file.name}`,
    };

    // CRITICAL: Backend DTO requires description at root level - ensure it's never empty
    if (!jsonPayload.description || typeof jsonPayload.description !== 'string' || !jsonPayload.description.trim()) {
      jsonPayload.description = formData?.creatorDescription?.trim()
        || aiMetadata?.data?.description?.trim()
        || 'Description pending';
    }

    // Add creatorDescription for non-G layers
    if (formData.layer !== 'G') {
      jsonPayload.creatorDescription = formData.creatorDescription || '';
    }

    // Validation - ensure required fields are not empty
    if (!jsonPayload.layer?.trim()) {
      console.error('❌ [JSON PAYLOAD] Layer is empty - this will cause 400 error');
    }
    if (!jsonPayload.category?.trim()) {
      console.error('❌ [JSON PAYLOAD] Category is empty - this will cause 400 error');
    }
    if (!jsonPayload.subcategory?.trim()) {
      console.error('❌ [JSON PAYLOAD] Subcategory is empty - this will cause 400 error');
    }
    if (!jsonPayload.description?.trim()) {
      console.error('❌ [JSON PAYLOAD] Description is empty - this will cause 400 error');
    }

    conditionalLog('📋 [PAYLOAD] Layer:', jsonPayload.layer, '| Fields:', Object.keys(jsonPayload).length);

    // All assets are base assets - never send baseAssetId or variant fields

    // PHASE 1 OPTIMIZATION: Single source of truth for layer metadata
    // Consolidate user and AI metadata to prevent 3x duplication

    // Apply field mapping to AI metadata for backend compatibility
    const mappedAIMetadata = aiMetadata?.data?.layerMetadata
      ? this.mapBackendToFrontendFields(aiMetadata.data.layerMetadata)
      : undefined;

    const consolidatedLayerMetadata = this.consolidateLayerMetadata(
      formData,
      mappedAIMetadata
    );

    // ANTI-BLOAT: Send only ONE metadata field based on layer type
    if (formData.layer === 'S' && formData.starMetadata) {
      // Stars layer: ONLY starMetadata
      const cleaned = this.cleanMetadataForSubmission(formData.starMetadata);
      jsonPayload.starMetadata = sanitizeMetadataForApi(cleaned, 'S');
      conditionalLog('✅ [ANTI-BLOAT] S layer: Sending starMetadata only');
    } else if (formData.layer === 'G' && consolidatedLayerMetadata) {
      // Songs layer: ONLY songMetadata (prevents songMetadata + layerMetadata duplication)
      const songMetadata = this.cleanMetadataForSubmission(consolidatedLayerMetadata);

      // CRITICAL: Ensure AI-generated description and tags are included in songMetadata
      if (aiMetadata?.data?.description && !songMetadata.description) {
        songMetadata.description = aiMetadata.data.description;
        conditionalLog('📝 [SONGS] Added AI description to songMetadata');
      }
      if (aiMetadata?.data?.tags && !songMetadata.tags) {
        songMetadata.tags = aiMetadata.data.tags;
        conditionalLog('🏷️ [SONGS] Added AI tags to songMetadata');
      }

      jsonPayload.songMetadata = sanitizeMetadataForApi(songMetadata, 'G');
      conditionalLog('✅ [ANTI-BLOAT] G layer: Sending songMetadata only (NO layerMetadata)');
    } else if (formData.layer === 'L' && formData.looksMetadata) {
      // PHASE 1 FIX (L-4): Looks layer sends looksMetadata
      const cleaned = this.cleanMetadataForSubmission(formData.looksMetadata);
      jsonPayload.looksMetadata = sanitizeMetadataForApi(cleaned, 'L');
      conditionalLog('✅ [PHASE 1] L layer: Sending looksMetadata only');
    } else if (formData.layer === 'M' && formData.movesMetadata) {
      // PHASE 1 FIX (M-1): Moves layer sends movesMetadata
      const cleaned = this.cleanMetadataForSubmission(formData.movesMetadata);
      jsonPayload.movesMetadata = sanitizeMetadataForApi(cleaned, 'M');
      conditionalLog('✅ [PHASE 1] M layer: Sending movesMetadata only');
    } else if (formData.layer === 'W' && formData.worldsMetadata) {
      // PHASE 1 FIX (W-3, W-4): Worlds layer sends worldsMetadata
      const cleaned = this.cleanMetadataForSubmission(formData.worldsMetadata);
      jsonPayload.worldsMetadata = sanitizeMetadataForApi(cleaned, 'W');
      conditionalLog('✅ [PHASE 1] W layer: Sending worldsMetadata only');
    } else if (consolidatedLayerMetadata) {
      // Other layers: ONLY layerMetadata (layer-agnostic design)
      jsonPayload.layerMetadata = this.cleanMetadataForSubmission(consolidatedLayerMetadata);
      conditionalLog('✅ [ANTI-BLOAT] Other layer: Sending layerMetadata only');
    }

    // CRITICAL: Ensure no duplicate metadata fields exist
    const metadataFieldCount = [
      jsonPayload.songMetadata,
      jsonPayload.starMetadata,
      jsonPayload.looksMetadata,
      jsonPayload.movesMetadata,
      jsonPayload.worldsMetadata,
      jsonPayload.layerMetadata
    ].filter(Boolean).length;

    if (metadataFieldCount > 1) {
      console.error('❌ [CRITICAL] Multiple metadata fields detected - clearing all and retrying');
      // Clear all metadata fields and set only the correct one
      delete jsonPayload.songMetadata;
      delete jsonPayload.starMetadata;
      delete jsonPayload.looksMetadata;
      delete jsonPayload.movesMetadata;
      delete jsonPayload.worldsMetadata;
      delete jsonPayload.layerMetadata;

      // Re-assign the correct field based on layer
      if (formData.layer === 'S' && formData.starMetadata) {
        const cleaned = this.cleanMetadataForSubmission(formData.starMetadata);
        jsonPayload.starMetadata = sanitizeMetadataForApi(cleaned, 'S');
      } else if (formData.layer === 'G' && consolidatedLayerMetadata) {
        // CRITICAL: For G layer, ALWAYS use songMetadata - NEVER layerMetadata
        const songMetadata = this.cleanMetadataForSubmission(consolidatedLayerMetadata);

        // CRITICAL: For G layer, description goes to ROOT level only (not songMetadata) to avoid duplication
        // Only add tags to songMetadata (description is already at root level)
        if (aiMetadata?.data?.tags && !songMetadata.tags) {
          songMetadata.tags = aiMetadata.data.tags;
        }

        // ANTI-BLOAT: Remove description from songMetadata to prevent duplication with root level
        if (songMetadata.description) {
          delete songMetadata.description;
        }

        jsonPayload.songMetadata = sanitizeMetadataForApi(songMetadata, 'G');
        // CRITICAL: Explicitly ensure no layerMetadata for G layer
        delete jsonPayload.layerMetadata;
      } else if (formData.layer === 'L' && formData.looksMetadata) {
        const cleaned = this.cleanMetadataForSubmission(formData.looksMetadata);
        jsonPayload.looksMetadata = sanitizeMetadataForApi(cleaned, 'L');
      } else if (formData.layer === 'M' && formData.movesMetadata) {
        const cleaned = this.cleanMetadataForSubmission(formData.movesMetadata);
        jsonPayload.movesMetadata = sanitizeMetadataForApi(cleaned, 'M');
      } else if (formData.layer === 'W' && formData.worldsMetadata) {
        const cleaned = this.cleanMetadataForSubmission(formData.worldsMetadata);
        jsonPayload.worldsMetadata = sanitizeMetadataForApi(cleaned, 'W');
      } else if (consolidatedLayerMetadata && formData.layer !== 'G') {
        // ONLY create layerMetadata for non-G/S/L/M/W layers
        jsonPayload.layerMetadata = this.cleanMetadataForSubmission(consolidatedLayerMetadata);
      }
    }

    // FINAL SAFETY CHECK: Ensure G layer NEVER has layerMetadata
    if (formData.layer === 'G' && jsonPayload.layerMetadata) {
      console.error('❌ [FINAL CHECK] layerMetadata detected for G layer - removing!');
      delete jsonPayload.layerMetadata;
    }

    // Include AI-generated metadata if available (non-duplicated fields only)
    if (aiMetadata?.data) {
      // MONGODB COMPATIBILITY: Add AI tags to root level for all layers (required by backend schema)
      if (aiMetadata.data.tags?.length > 0) {
        jsonPayload.tags = aiMetadata.data.tags;
        conditionalLog(`🏷️ [MONGODB] Added ${aiMetadata.data.tags.length} AI tags to root level`);
      }

      // For G layer, description goes to root level (songMetadata description will be removed later)
      if (formData.layer === 'G') {
        if (aiMetadata.data.description) {
          jsonPayload.description = aiMetadata.data.description;
          conditionalLog('📝 [MONGODB] Added AI description to root level for G layer');
        }
      } else {
        // For non-G layers, add AI description as aiDescription
        if (aiMetadata.data.description) {
          jsonPayload.aiDescription = aiMetadata.data.description;
        }
      }
    }

    // COMPOSITE LAYER: Handle components array for Composite assets (C layer)
    if (formData.layer === 'C' && formData.components) {
      conditionalLog('🎼 [COMPOSITE] Processing components array for Composite asset');
      conditionalLog('🎼 [COMPOSITE] Component count:', formData.components.length);

      // Transform components to the format backend expects
      const componentAssets = formData.components.map((component: any) => {
        const componentData = {
          assetId: component.friendlyName || component.name || component.id || component._id,
          layer: component.layer,
          // Include minimal metadata - backend will fetch full details if needed
        };
        return componentData;
      });

      // Add both componentAssets (array of IDs/HFNs) and components (clean serializable objects)
      const userSelectedComponentHFNs = componentAssets.map((c: any) => c.assetId);
      jsonPayload.componentAssets = userSelectedComponentHFNs;

      // CRITICAL FIX: Create clean, serializable component objects (no DOM/React references)
      jsonPayload.components = formData.components.map((component: any) => ({
        id: component.id || component._id,
        _id: component._id,
        name: component.name || component.friendlyName,
        friendlyName: component.friendlyName || component.name,
        layer: component.layer,
        category: component.category,
        subcategory: component.subcategory,
        nnaAddress: component.nnaAddress || component.nna_address,
        nna_address: component.nna_address || component.nnaAddress,
        // Only include serializable metadata, no functions or DOM references
      }));

      conditionalLog('🎼 [COMPOSITE] Added to payload:');
      conditionalLog('  - componentAssets:', JSON.stringify(jsonPayload.componentAssets));
      conditionalLog('  - components count:', jsonPayload.components.length);

      // CRITICAL: Ensure componentAssets is in layerMetadata (backend validation requirement)
      // Initialize layerMetadata if it doesn't exist
      if (!jsonPayload.layerMetadata) {
        conditionalLog('⚠️ [COMPOSITE] layerMetadata missing - initializing for C layer');
        jsonPayload.layerMetadata = {};
      }

      // Add or override componentAssets in layerMetadata
      if (jsonPayload.layerMetadata.componentAssets) {
        conditionalLog('🔧 [COMPOSITE FIX] Overriding AI dummy componentAssets with user selections');
      }

      jsonPayload.layerMetadata.componentAssets = userSelectedComponentHFNs;
      conditionalLog('✅ [COMPOSITE FIX] layerMetadata.componentAssets set');
    }

    // ANTI-BLOAT CRITICAL: Prevent duplicate fields at root level that exist in metadata
    const songSpecificFields = [
      'songName', 'artistName', 'albumName', 'albumArt', 'albumArtUrl', 'artworkUrl',
      'genre', 'mood', 'energy', 'danceability', 'bpm', 'key', 'tempo', 'timeSignature',
      'vocalType', 'originalArtistGender', 'genderSuitability', 'releaseYear',
      'culturalOrigin', 'language', 'ageAppropriateness', 'popularity', 'explicit',
      'duration', 'instruments', 'themes', 'style', 'production', 'recordingQuality',
      'valence', 'acousticness', 'instrumentalness', 'liveness', 'speechiness',
      'tags', 'description', 'aiDescription' // CRITICAL: These must be in songMetadata only
    ];

    // Additional flexible fields
    Object.entries(formData).forEach(([key, value]) => {
      // Skip fields we've already handled or that would create duplicate metadata
      if (['file', 'layer', 'humanFriendlyName', 'assetType', 'category', 'subcategory',
           'baseAssetId', 'baseStarId', 'baseSongId', 'baseLookId', 'baseMoveId', 'baseWorldId', 'baseBrandId', 'baseCompositeId',
           'variantName', 'variantType', 'variantDescription',
           'starMetadata', 'songMetadata', 'looksMetadata', 'movesMetadata', 'worldsMetadata', 'layerMetadata',
           'components', 'componentAssets'].includes(key)) {
        return;
      }

      // CRITICAL: For G layer, ONLY allow specific root fields - skip ALL others
      if (formData.layer === 'G') {
        const allowedRootFields = ['name', 'source', 'fileUrl', 'creatorDescription', 'description'];
        if (!allowedRootFields.includes(key)) {
          // Skip silently to reduce log verbosity
          return;
        }
      }

      // CRITICAL: Skip song-specific fields to prevent duplication with songMetadata
      if (formData.layer === 'G' && songSpecificFields.includes(key)) {
        // Skip silently to reduce log verbosity
        return;
      }

      if (value !== undefined && value !== null) {
        try {
          if (typeof value === 'object' && !(value instanceof File) && !(value instanceof Blob)) {
            // Clean the metadata object before adding to JSON
            const cleanValue = this.cleanMetadataForSubmission(value);
            // Only include if we got valid clean data
            if (Object.keys(cleanValue).length > 0) {
              jsonPayload[key] = cleanValue;
            }
          } else {
            jsonPayload[key] = value;
          }
        } catch (error) {
          console.warn(`⚠️ [REGISTRATION SERVICE] Failed to process ${key}:`, error);
          // Skip this field if it can't be processed
        }
      }
    });

    // Debug: Show which metadata fields are present to identify anti-bloat violations
    const metadataFields = ['songMetadata', 'starMetadata', 'looksMetadata', 'movesMetadata', 'worldsMetadata', 'layerMetadata'];
    const presentMetadataFields = metadataFields.filter(field => jsonPayload[field]);
    if (presentMetadataFields.length > 1) {
      console.error('❌ [ANTI-BLOAT VIOLATION] Multiple metadata fields detected:', presentMetadataFields);
    }

    // Anti-bloat validation: Check for duplicate fields (reduced logging)
    if (formData.layer === 'G' && jsonPayload.songMetadata) {
      const rootKeys = Object.keys(jsonPayload);
      const songMetadataKeys = Object.keys(jsonPayload.songMetadata);
      const duplicateFields = rootKeys.filter(key => songMetadataKeys.includes(key));

      if (duplicateFields.length > 0) {
        console.error('❌ [ANTI-BLOAT] Duplicate fields found:', duplicateFields);
      }

      conditionalLog(`🔍 [DEBUG] Payload: ${rootKeys.length} root fields, ${songMetadataKeys.length} song fields`);
    }

    conditionalLog('📤 [SUBMIT] Payload ready with', presentMetadataFields.join(', '), 'metadata');

    // CRITICAL DEBUG: Log the actual payload structure being sent to identify validation issues
    const debugPayload = { ...jsonPayload };
    if (debugPayload.fileData) debugPayload.fileData = '[BASE64_DATA_MASKED]';

    const rootFields = Object.keys(debugPayload).filter(key => key !== 'fileData');
    conditionalLog('📦 [PAYLOAD] Root-level fields:', rootFields);

    if (debugPayload.songMetadata) {
      conditionalLog('🎵 [SONGS PAYLOAD] songMetadata fields:', Object.keys(debugPayload.songMetadata));

      // Check for potential validation issues
      const potentialIssues = [];
      if (!debugPayload.songMetadata.songName || debugPayload.songMetadata.songName === 'Unknown Song') {
        potentialIssues.push('Invalid songName');
      }
      if (!debugPayload.songMetadata.artistName || debugPayload.songMetadata.artistName === 'Unknown Artist') {
        potentialIssues.push('Invalid artistName');
      }

      if (potentialIssues.length > 0) {
        console.error('❌ [SONGS PAYLOAD] Validation issues:', potentialIssues);
      }
    }

    // Check for duplicate fields at root that might exist in songMetadata
    const duplicateFields: string[] = [];

    if (debugPayload.songMetadata) {
      // Check EVERY field in songMetadata against root
      Object.keys(debugPayload.songMetadata).forEach(key => {
        if (debugPayload[key] !== undefined) {
          duplicateFields.push(key);
        }
      });
    }

    // Only log critical duplicate issues
    if (duplicateFields.length > 0) {
      console.error('❌ [DUPLICATES]:', duplicateFields.length, 'fields duplicated');
    }

    // ULTIMATE SAFETY CHECK: Strip layerMetadata completely for G layer before submission
    if (formData.layer === 'G' && jsonPayload.layerMetadata) {
      console.error('❌ [ULTIMATE SAFETY] Removing layerMetadata from G layer payload before submission!');
      delete jsonPayload.layerMetadata;
    }

    // FINAL ANTI-BLOAT CHECK: Remove duplicate fields from songMetadata for G layer
    if (formData.layer === 'G' && jsonPayload.songMetadata) {
      // Remove description and tags from songMetadata to avoid duplication with root
      if (jsonPayload.songMetadata.description) {
        conditionalLog('🔧 [FINAL CLEANUP] Removing description from songMetadata to prevent duplication');
        delete jsonPayload.songMetadata.description;
      }
      if (jsonPayload.songMetadata.tags) {
        conditionalLog('🔧 [FINAL CLEANUP] Removing tags from songMetadata to prevent duplication');
        delete jsonPayload.songMetadata.tags;
      }
    }

    // 🔍 COMPREHENSIVE PAYLOAD INSPECTION - Final payload before submission
    conditionalLog('🔍 [PAYLOAD INSPECTION] Final payload structure before API submission:');
    conditionalLog('📋 Root level fields:', Object.keys(jsonPayload));
    conditionalLog('🎵 songMetadata fields:', Object.keys(jsonPayload.songMetadata || {}));
    conditionalLog('📊 layerMetadata fields:', jsonPayload.layerMetadata ? Object.keys(jsonPayload.layerMetadata) : 'None');

    // Check for potential duplicate fields
    const payloadRootFields = Object.keys(jsonPayload).filter(key => !['songMetadata', 'layerMetadata'].includes(key));
    const songFields = jsonPayload.songMetadata ? Object.keys(jsonPayload.songMetadata) : [];
    const layerFields = jsonPayload.layerMetadata ? Object.keys(jsonPayload.layerMetadata) : [];

    const duplicatesWithSong = payloadRootFields.filter(field => songFields.includes(field));
    const duplicatesWithLayer = payloadRootFields.filter(field => layerFields.includes(field));

    if (duplicatesWithSong.length > 0) {
      console.error('❌ [DUPLICATE DETECTION] Fields in both root and songMetadata:', duplicatesWithSong);
    }
    if (duplicatesWithLayer.length > 0) {
      console.error('❌ [DUPLICATE DETECTION] Fields in both root and layerMetadata:', duplicatesWithLayer);
    }
    if (jsonPayload.songMetadata && jsonPayload.layerMetadata) {
      console.error('❌ [DUPLICATE DETECTION] Both songMetadata and layerMetadata present!');
    }

    // 🛡️ FINAL GUARD: Absolute last-chance cleanup before fetch - remove ALL root field duplicates
    if (jsonPayload.songMetadata) {
      const rootFields = Object.keys(jsonPayload).filter(key => key !== 'songMetadata' && key !== 'layerMetadata' && key !== 'starMetadata');
      const songFields = Object.keys(jsonPayload.songMetadata);
      const duplicateFields = songFields.filter(field => rootFields.includes(field));

      // 🔧 CRITICAL FIX: Remove duplicate fields from ROOT (not from songMetadata!)
      // songMetadata should be the source of truth for layer-specific fields
      duplicateFields.forEach(field => {
        delete jsonPayload[field];  // Remove from ROOT, keep in songMetadata
        conditionalLog(`🔧 [FINAL GUARD] Removed duplicate field from root: ${field}`);
      });

      conditionalLog('🔍 [POST-GUARD] Root fields after final guard:', Object.keys(jsonPayload).filter(k => k !== 'songMetadata'));
    }

    // 🔧 FIELD TYPE FIX: Ensure certain fields are arrays as backend might expect
    if (jsonPayload.songMetadata) {
      // Ensure genre is an array
      if (jsonPayload.songMetadata.genre && !Array.isArray(jsonPayload.songMetadata.genre)) {
        jsonPayload.songMetadata.genre = [jsonPayload.songMetadata.genre];
      }

      // Ensure mood is an array
      if (jsonPayload.songMetadata.mood && !Array.isArray(jsonPayload.songMetadata.mood)) {
        jsonPayload.songMetadata.mood = [jsonPayload.songMetadata.mood];
      }

      // Ensure instruments is an array (if present)
      if (jsonPayload.songMetadata.instruments && !Array.isArray(jsonPayload.songMetadata.instruments)) {
        jsonPayload.songMetadata.instruments = [jsonPayload.songMetadata.instruments];
      }

      // Ensure themes is an array (if present)
      if (jsonPayload.songMetadata.themes && !Array.isArray(jsonPayload.songMetadata.themes)) {
        jsonPayload.songMetadata.themes = [jsonPayload.songMetadata.themes];
      }
    }

    try {
      // CRITICAL FIX: Remove React event properties that may have leaked into payload
      // These properties (_reactName, type, target, bubbles, etc.) cause JSON serialization issues
      const reactEventProps = [
        '_reactName', 'type', 'target', 'currentTarget', 'eventPhase', 'bubbles',
        'cancelable', 'timeStamp', 'defaultPrevented', 'isTrusted', 'detail',
        'screenX', 'screenY', 'clientX', 'clientY', 'pageX', 'pageY',
        'ctrlKey', 'shiftKey', 'altKey', 'metaKey', 'getModifierState',
        'button', 'buttons', 'movementX', 'movementY', 'isDefaultPrevented',
        'isPropagationStopped', 'nativeEvent', 'preventDefault', 'stopPropagation',
        'persist', 'isPersistent'
      ];

      reactEventProps.forEach(prop => {
        if (prop in jsonPayload) {
          conditionalLog(`⚠️ [PAYLOAD CLEANUP] Removing React event property: ${prop}`);
          delete jsonPayload[prop];
        }
      });

      conditionalLog('📡 [REGISTRATION SERVICE] Submitting to POST /api/assets/json (JSON endpoint)');

      const result = await this.makeRequest('/assets/json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(jsonPayload),
      });

      conditionalLog(`📊 Status: ${result.status}`);

      if (result.status === 200 || result.status === 201) {
        console.log('✅ Asset created successfully');
        return this.normalizeCreationResponse(result.data);
      }
      if (result.status === 204) {
        console.log('✅ Asset created successfully (204)');
        return this.normalizeCreationResponse(result.data);
      }
      if (isAPIError(result.data)) {
        console.error(`❌ [REGISTRATION SERVICE] API Error: ${result.data.message}`);
        console.error(`❌ [REGISTRATION SERVICE] Full API Error Response:`, JSON.stringify(result.data, null, 2));
        const msg = formatStructuredApiErrorResponse(result.data, result.data.message);
        throw new Error(msg || `Asset creation failed: ${result.data.message}`);
      }
      {
        console.error(`❌ [REGISTRATION SERVICE] Unexpected response - Status: ${result.status}`);
        console.error(`❌ [REGISTRATION SERVICE] Response data:`, result.data);

        let errorMessage = 'Unknown error';
        if (result.status === 400) {
          errorMessage = 'Bad Request - Validation failed. Check the console for detailed error information.';
        }
        errorMessage = formatStructuredApiErrorResponse(result.data, errorMessage);
        if (typeof result.data === 'string') {
          errorMessage = result.data;
        }

        throw new Error(`Asset creation failed (${result.status}): ${errorMessage}`);
      }
    } catch (error: unknown) {
      console.error('❌ [REGISTRATION SERVICE] Error creating asset:', error);
      throw error;
    }
  }

  /**
   * Complete registration workflow - combines AI metadata generation and asset creation
   */
  async registerAsset(formData: RegistrationFormData): Promise<{
    aiMetadata: AIMetadataResponse;
    asset: AssetCreationResponse;
  }> {
    conditionalLog('🔄 [REGISTRATION SERVICE] Starting complete registration workflow');

    try {
      // Step 1: Generate AI metadata
      const aiMetadata = await this.generateAIMetadata(formData);

      // Step 2: Create asset with AI metadata
      const asset = await this.createAsset(formData, aiMetadata);

      return { aiMetadata, asset };
    } catch (error) {
      console.error('❌ [REGISTRATION SERVICE] Complete registration failed:', error);
      throw error;
    }
  }

  /**
   * Register song asset using the new /assets/song endpoint
   * Uses multipart/form-data with assetFile and assetData
   */
  async registerSongAsset(formData: RegistrationFormData, aiMetadata?: AIMetadataResponse): Promise<AssetCreationResponse> {
    conditionalLog('🎵 [REGISTRATION SERVICE] Registering song asset with new endpoint');

    if (!formData.file) {
      throw new Error('File is required for song asset creation');
    }
    const songAssetPayload = this.buildSongAssetRequest(formData);
    const formDataObj = this.buildAssetFormData(formData.file, songAssetPayload);

    conditionalLog('📤 [SONG REGISTRATION] Submitting to /api/assets/song', {
      fileName: formData.file.name,
      songName: songAssetPayload.metadata.songName,
      artistName: songAssetPayload.metadata.artistName,
      hasAlgorhythmMetadata: !!songAssetPayload.metadata.algorhythmMetadata
    });

    try {
      const result = await this.makeRequest('/assets/song', {
        method: 'POST',
        body: formDataObj,
      });

      if (result.status === 200 || result.status === 201) {
        conditionalLog('✅ [SONG REGISTRATION] Song asset created successfully');
        return this.normalizeCreationResponse(result.data);
      }
      if (result.status === 204) {
        conditionalLog('✅ [SONG REGISTRATION] Song asset created successfully (204)');
        return this.normalizeCreationResponse(result.data);
      }
      const msg = formatStructuredApiErrorResponse(result.data);
      throw new Error(msg || `Song registration failed: ${result.status}`);
    } catch (error) {
      conditionalError('❌ [SONG REGISTRATION] Failed to register song asset:', error);
      throw error;
    }
  }

  /**
   * Register star asset using the new /assets/star endpoint
   * Uses multipart/form-data with assetFile and assetData.
   * All assets are base assets - no variant fields are sent.
   */
  async registerStarAsset(formData: RegistrationFormData, aiMetadata?: AIMetadataResponse): Promise<AssetCreationResponse> {
    conditionalLog('⭐ [REGISTRATION SERVICE] Registering star asset with new endpoint');

    if (!formData.file) {
      throw new Error('File is required for star asset creation');
    }
    const starAssetPayload = this.buildStarAssetRequest(formData, aiMetadata);
    const formDataObj = this.buildAssetFormData(formData.file, starAssetPayload);

    conditionalLog('📤 [STAR REGISTRATION] Submitting to /api/assets/star', {
      fileName: formData.file.name,
      starName: starAssetPayload.metadata.starName,
    });

    try {
      const result = await this.makeRequest('/assets/star', {
        method: 'POST',
        body: formDataObj,
      });

      if (result.status === 200 || result.status === 201) {
        conditionalLog('✅ [STAR REGISTRATION] Star asset created successfully');
        return this.normalizeCreationResponse(result.data);
      }
      if (result.status === 204) {
        conditionalLog('✅ [STAR REGISTRATION] Star asset created successfully (204)');
        return this.normalizeCreationResponse(result.data);
      }
      const msg = formatStructuredApiErrorResponse(result.data);
      throw new Error(msg || `Star registration failed: ${result.status}`);
    } catch (error) {
      conditionalError('❌ [STAR REGISTRATION] Failed to register star asset:', error);
      throw error;
    }
  }

  /**
   * Register move asset using the new /assets/move endpoint
   * Uses multipart/form-data with assetFile and assetData.
   * All assets are base assets - no variant fields are sent.
   */
  async registerMoveAsset(formData: RegistrationFormData, aiMetadata?: AIMetadataResponse): Promise<AssetCreationResponse> {
    conditionalLog('💃 [REGISTRATION SERVICE] Registering move asset with new endpoint');

    if (!formData.file) {
      throw new Error('File is required for move asset creation');
    }
    const moveAssetPayload = this.buildMoveAssetRequest(formData, aiMetadata);
    const formDataObj = this.buildAssetFormData(formData.file, moveAssetPayload);

    conditionalLog('📤 [MOVE REGISTRATION] Submitting to /api/assets/move', {
      fileName: formData.file.name,
      moveName: moveAssetPayload.metadata.moveName,
    });

    try {
      const result = await this.makeRequest('/assets/move', {
        method: 'POST',
        body: formDataObj,
      });

      if (result.status === 200 || result.status === 201) {
        conditionalLog('✅ [MOVE REGISTRATION] Move asset created successfully');
        return this.normalizeCreationResponse(result.data);
      }
      if (result.status === 204) {
        conditionalLog('✅ [MOVE REGISTRATION] Move asset created successfully (204)');
        return this.normalizeCreationResponse(result.data);
      }
      const msg = formatStructuredApiErrorResponse(result.data);
      throw new Error(msg || `Move registration failed: ${result.status}`);
    } catch (error) {
      conditionalError('❌ [MOVE REGISTRATION] Failed to register move asset:', error);
      throw error;
    }
  }

  /**
   * Register look asset using the new /assets/look endpoint
   * Uses multipart/form-data with assetFile and assetData.
   * All assets are base assets - no variant fields are sent.
   */
  async registerLookAsset(formData: RegistrationFormData, aiMetadata?: AIMetadataResponse): Promise<AssetCreationResponse> {
    conditionalLog('👗 [REGISTRATION SERVICE] Registering look asset with new endpoint');

    if (!formData.file) {
      throw new Error('File is required for look asset creation');
    }
    const lookAssetPayload = this.buildLookAssetRequest(formData, aiMetadata);
    const formDataObj = this.buildAssetFormData(formData.file, lookAssetPayload);

    conditionalLog('📤 [LOOK REGISTRATION] Submitting to /api/assets/look', {
      fileName: formData.file.name,
      outfitName: lookAssetPayload.metadata.outfitName,
    });

    try {
      const result = await this.makeRequest('/assets/look', {
        method: 'POST',
        body: formDataObj,
      });

      if (result.status === 200 || result.status === 201) {
        conditionalLog('✅ [LOOK REGISTRATION] Look asset created successfully');
        return this.normalizeCreationResponse(result.data);
      }
      if (result.status === 204) {
        conditionalLog('✅ [LOOK REGISTRATION] Look asset created successfully (204)');
        return this.normalizeCreationResponse(result.data);
      }
      const msg = formatStructuredApiErrorResponse(result.data);
      throw new Error(msg || `Look registration failed: ${result.status}`);
    } catch (error) {
      conditionalError('❌ [LOOK REGISTRATION] Failed to register look asset:', error);
      throw error;
    }
  }

  /**
   * Register world asset using the new /assets/world endpoint
   * Uses multipart/form-data with assetFile and assetData.
   * All assets are base assets - no variant fields are sent.
   */
  async registerWorldAsset(formData: RegistrationFormData, aiMetadata?: AIMetadataResponse): Promise<AssetCreationResponse> {
    conditionalLog('🌍 [REGISTRATION SERVICE] Registering world asset with new endpoint');

    if (!formData.file) {
      throw new Error('File is required for world asset creation');
    }
    const worldAssetPayload = this.buildWorldAssetRequest(formData, aiMetadata);
    const formDataObj = this.buildAssetFormData(formData.file, worldAssetPayload);

    conditionalLog('📤 [WORLD REGISTRATION] Submitting to /api/assets/world', {
      fileName: formData.file.name,
      worldName: worldAssetPayload.metadata.worldName,
    });

    try {
      const result = await this.makeRequest('/assets/world', {
        method: 'POST',
        body: formDataObj,
      });

      if (result.status === 200 || result.status === 201) {
        conditionalLog('✅ [WORLD REGISTRATION] World asset created successfully');
        return this.normalizeCreationResponse(result.data);
      }
      if (result.status === 204) {
        conditionalLog('✅ [WORLD REGISTRATION] World asset created successfully (204)');
        return this.normalizeCreationResponse(result.data);
      }
      const msg = formatStructuredApiErrorResponse(result.data);
      throw new Error(msg || `World registration failed: ${result.status}`);
    } catch (error) {
      conditionalError('❌ [WORLD REGISTRATION] Failed to register world asset:', error);
      throw error;
    }
  }

  /**
   * Register composite asset using POST /assets/composite.
   * Same request structure as world: multipart with assetFile and assetData (JSON).
   * assetData.metadata is { songId, starId, lookId, moveId, worldId } from the 5 selected components.
   */
  async registerCompositeAsset(formData: RegistrationFormData): Promise<AssetCreationResponse> {
    conditionalLog('📦 [REGISTRATION SERVICE] Registering composite asset with /assets/composite');

    if (!formData.file) {
      throw new Error('File is required for composite asset creation');
    }

    const components = (formData as any).components ?? (formData as any).layerSpecificData?.components ?? [];
    const layerToKey: Record<string, string> = {
      G: 'songId',
      S: 'starId',
      L: 'lookId',
      M: 'moveId',
      W: 'worldId',
    };
    const metadata: Record<string, string> = {
      songId: '',
      starId: '',
      lookId: '',
      moveId: '',
      worldId: '',
    };
    if (Array.isArray(components)) {
      for (const comp of components) {
        const layer = comp?.layer;
        const key = layer && layerToKey[layer];
        if (key) {
          metadata[key] = comp?.id ?? comp?._id ?? '';
        }
      }
    }

    const formTags = Array.isArray((formData as any).tags) ? (formData as any).tags : [];
    const tagsNonEmpty = formTags.length > 0 ? formTags : ['user_upload'];

    const creatorDescription = (formData.creatorDescription ?? (formData as any).description ?? (formData as any).name ?? '').toString().trim() || 'no description';
    const description = ((formData as any).description ?? formData.creatorDescription ?? (formData as any).name ?? '').toString().trim() || '';

    // API expects flat JSON: component IDs and fields at root (no nested metadata)
    const compositeAssetPayload: Record<string, unknown> = {
      category: formData.category,
      subcategory: formData.subcategory,
      description,
      creatorDescription,
      tags: tagsNonEmpty,
      songId: metadata.songId,
      starId: metadata.starId,
      lookId: metadata.lookId,
      moveId: metadata.moveId,
      worldId: metadata.worldId,
    };

    const formDataObj = new FormData();
    formDataObj.append('assetFile', formData.file);
    formDataObj.append('assetData', JSON.stringify(compositeAssetPayload));

    conditionalLog('📤 [COMPOSITE REGISTRATION] Submitting to /api/assets/composite', {
      fileName: formData.file.name,
      payload: compositeAssetPayload,
    });

    try {
      const result = await this.makeRequest('/assets/composite', {
        method: 'POST',
        body: formDataObj,
      });

      if (result.status === 200 || result.status === 201) {
        conditionalLog('✅ [COMPOSITE REGISTRATION] Composite asset created successfully');
        return this.normalizeCreationResponse(result.data);
      }
      if (result.status === 204) {
        conditionalLog('✅ [COMPOSITE REGISTRATION] Composite asset created successfully (204)');
        return this.normalizeCreationResponse(result.data);
      }
      const msg = formatStructuredApiErrorResponse(result.data);
      throw new Error(msg || `Composite registration failed: ${result.status}`);
    } catch (error) {
      conditionalError('❌ [COMPOSITE REGISTRATION] Failed to register composite asset:', error);
      throw error;
    }
  }

  /**
   * Generate asset name (HFN) based on layer, category, subcategory
   * Backend will assign the actual sequence number
   */
  private generateAssetName(formData: RegistrationFormData): string {
    const { layer, category, subcategory } = formData;

    // Safety check: Ensure all required fields are present
    if (!layer || !category || !subcategory) {
      console.error('❌ [ASSET NAME] Missing required fields for HFN generation:', {
        layer: layer || 'MISSING',
        category: category || 'MISSING',
        subcategory: subcategory || 'MISSING'
      });

      // Return a safe fallback that won't break backend
      const fallbackName = `${layer || 'UNK'}.${category || 'UNK'}.${subcategory || 'UNK'}.000`;
      return fallbackName;
    }

    // Generate placeholder HFN - backend will assign actual sequence number
    const placeholderName = `${layer}.${category}.${subcategory}.000`;

    conditionalLog(`🏷️ [ASSET NAME] Generated placeholder name: ${placeholderName}`);
    return placeholderName;
  }

  /**
   * Register a variant for a base asset.
   * POST {basePath}/variants with multipart/form-data: assetFile + variantData (JSON).
   * On success returns; on 4xx/5xx throws with message from response body if present.
   */
  async registerVariant(
    layer: string,
    file: File,
    variantData: RegisterVariantPayload
  ): Promise<void> {
    if (!isVariantLayer(layer)) {
      throw new Error(`Layer "${layer}" does not support variants`);
    }
    const pathSegment = LAYER_TO_VARIANTS_PATH[layer];
    const endpoint = `/assets/${pathSegment}/variants`;

    const formData = new FormData();
    formData.append('assetFile', file);
    formData.append('variantData', JSON.stringify(variantData));

    conditionalLog(`📤 [VARIANT] POST ${endpoint}`, {
      baseAssetId: variantData.baseAssetId,
      variantName: variantData.variantName,
      variantType: variantData.variantType,
      fileName: file.name,
    });

    const result = await this.makeRequest(endpoint, {
      method: 'POST',
      body: formData,
    });

    if (result.status === 204) {
      conditionalLog('✅ [VARIANT] Variant registered successfully (204)');
      return;
    }
    if (result.status === 200 || result.status === 201) {
      conditionalLog('✅ [VARIANT] Variant registered successfully');
      return;
    }

    const d = result.data;
    const structuredMessage =
      typeof d === 'object' && d !== null
        ? formatStructuredApiErrorResponse(d)
        : undefined;
    const message =
      structuredMessage && structuredMessage !== 'Unknown error'
        ? structuredMessage
        : typeof d === 'object' && d !== null
          ? (d.message ?? d.error ?? d.detail ?? (Array.isArray(d.errors) ? d.errors.join('; ') : undefined))
          : typeof d === 'string'
            ? d
            : undefined;
    throw new Error(message ? String(message) : `Variant registration failed: HTTP ${result.status}`);
  }
}

export const registrationService = new RegistrationService();
